import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { Loader2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { db } from '@/lib/firebase/config';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  getGroupName,
  isGroupMember,
  joinGroup,
  SectionNotJoinableError,
  SubgroupConflictError,
} from '@/lib/firebase/queries/group-members';
import { membershipConflictMessage, sectionNotJoinableMessage } from '@/lib/groups/membershipConflictCopy';
import { groupAppUrl, inviteDevice, inviteGroupId, storeLinks } from '@/lib/groups/groupInvite';

interface InviteGroup {
  name: string;
  description?: string;
  photoURL?: string | null;
  isSection: boolean;
}

/**
 * `/group/:groupId` — the group invite link the app shares. With the app
 * installed the phone opens the app directly (universal / app links); this
 * page is what everyone else sees: the group, «open in the app», the store,
 * and joining from the website when signed in (same membership rules as the
 * chat's join button: one group per organization, sections not joinable).
 */
export function GroupInvitePage() {
  const { groupId: rawId } = useParams();
  const groupId = inviteGroupId(rawId);
  const navigate = useNavigate();
  const { currentUser, loading: authLoading } = useAuth();
  const { t, language, dir } = useLanguage();
  const [group, setGroup] = useState<InviteGroup | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'missing'>('loading');
  const [isMember, setIsMember] = useState(false);
  const [joining, setJoining] = useState(false);
  const stores = useMemo(() => storeLinks(inviteDevice(navigator.userAgent)), []);

  useEffect(() => {
    if (!groupId) {
      setState('missing');
      return;
    }
    let alive = true;
    getDoc(doc(db, 'supportGroups', groupId))
      .then((snap) => {
        if (!alive) return;
        if (!snap.exists()) {
          setState('missing');
          return;
        }
        const data = snap.data();
        setGroup({
          name: data.name || '',
          description: data.description || '',
          photoURL: data.photoURL || null,
          isSection: data.groupType === 'parent',
        });
        setState('ready');
      })
      .catch(() => alive && setState('missing'));
    return () => { alive = false; };
  }, [groupId]);

  useEffect(() => {
    if (!groupId || !currentUser?.uid) return;
    let alive = true;
    isGroupMember(groupId, currentUser.uid).then((m) => alive && setIsMember(m));
    return () => { alive = false; };
  }, [groupId, currentUser?.uid]);

  const openGroups = () => navigate('/dashboard/customer/support');

  const signInToJoin = () => {
    if (!groupId) return;
    sessionStorage.setItem('redirectAfterLogin', `/group/${groupId}`);
    navigate('/login');
  };

  const join = async () => {
    if (!groupId || !currentUser) return;
    setJoining(true);
    try {
      const ok = await joinGroup(groupId, currentUser.uid);
      if (ok) {
        toast.success(t('support.joinedGroup'));
        openGroups();
      } else {
        toast.error(t('support.errorJoiningGroup'));
      }
    } catch (error) {
      if (error instanceof SubgroupConflictError) {
        toast.error(membershipConflictMessage('self', language === 'ar', await getGroupName(error.existingGroupId)));
      } else if (error instanceof SectionNotJoinableError) {
        toast.error(sectionNotJoinableMessage(language === 'ar'));
      } else {
        toast.error(t('support.errorJoiningGroup'));
      }
    } finally {
      setJoining(false);
    }
  };

  if (state === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div dir={dir} className="min-h-screen bg-muted/30 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardContent className="p-6 flex flex-col items-center gap-4 text-center">
          <img src="/namatplatformlogo.png" alt="Namat" className="h-10" />
          {state === 'missing' || !group ? (
            <p className="text-muted-foreground" data-testid="invite-missing">{t('support.invite.notFound')}</p>
          ) : (
            <>
              {group.photoURL ? (
                <img src={group.photoURL} alt="" className="h-20 w-20 rounded-full object-cover" />
              ) : (
                <div className="h-20 w-20 rounded-full bg-[#004250] flex items-center justify-center">
                  <Users className="h-9 w-9 text-white" />
                </div>
              )}
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">{t('support.invite.title')}</p>
                <h1 className="text-2xl font-bold text-[#004250]" data-testid="invite-group-name">{group.name}</h1>
                {group.description ? <p className="text-sm text-muted-foreground">{group.description}</p> : null}
              </div>

              <Button asChild className="w-full bg-[#7dda55] hover:bg-[#6dc447] text-white">
                <a href={groupAppUrl(groupId!)}>{t('support.invite.openInApp')}</a>
              </Button>

              <div className="w-full space-y-2">
                <p className="text-xs text-muted-foreground">{t('support.invite.noApp')}</p>
                <div className="flex gap-2 justify-center flex-wrap">
                  {stores.map((s) => (
                    <Button key={s.store} asChild variant="outline">
                      <a href={s.url} target="_blank" rel="noopener noreferrer">
                        {s.store === 'apple' ? t('support.invite.appStore') : t('support.invite.playStore')}
                      </a>
                    </Button>
                  ))}
                </div>
              </div>

              {!group.isSection && !authLoading && (
                <div className="w-full border-t pt-4">
                  {!currentUser ? (
                    <Button variant="ghost" className="w-full" onClick={signInToJoin}>
                      {t('support.invite.signInToJoin')}
                    </Button>
                  ) : isMember ? (
                    <Button variant="ghost" className="w-full" onClick={openGroups}>
                      {t('support.invite.openOnWeb')}
                    </Button>
                  ) : (
                    <Button variant="ghost" className="w-full" onClick={join} disabled={joining}>
                      {joining ? <Loader2 className="h-4 w-4 animate-spin" /> : t('support.invite.joinOnWeb')}
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
